import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  ApiError,
  deleteMyAvatar,
  enrollRecoveryCodes,
  fetchLanguageCatalog,
  fetchMe,
  listPasskeys,
  listSessions,
  logoutAll,
  patchProfile,
  registerPasskey,
  revokePasskey,
  revokeSession,
  rotateRecoveryCodes,
  stepUpPasskey,
  totpConfirm,
  totpStart,
  uploadMyAvatar,
  type AuthSessionRow,
} from '../lib/api'
import { setSession, getSnapshot } from '../lib/session'
import { useSession } from '../lib/useSession'
import { useUiStore } from '../lib/uiStore'
import { Icon } from './Icon'
import { StepUpModal } from './StepUpModal'
import { UserAvatar } from './UserAvatar'

type Props = {
  avatarRevision: number
  onAvatarChange: () => void
}

export function ProfilePanel({ avatarRevision, onAvatarChange }: Props) {
  const { t, i18n } = useTranslation()
  const { user, amr } = useSession()
  const locale = useUiStore((s) => s.locale)
  const setLocale = useUiStore((s) => s.setLocale)

  const meQ = useQuery({ queryKey: ['auth-me'], queryFn: fetchMe })
  const passkeysQ = useQuery({ queryKey: ['passkeys'], queryFn: listPasskeys })
  const sessionsQ = useQuery({ queryKey: ['sessions'], queryFn: listSessions })
  const langsQ = useQuery({
    queryKey: ['i18n-languages'],
    queryFn: fetchLanguageCatalog,
    staleTime: 60 * 60 * 1000,
  })

  const [displayName, setDisplayName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)
  const [needStepUp, setNeedStepUp] = useState<null | (() => void)>(null)
  const [codes, setCodes] = useState<string[] | null>(null)
  const [totpUri, setTotpUri] = useState<string | null>(null)
  const [totpSecret, setTotpSecret] = useState<string | null>(null)
  const [totpCode, setTotpCode] = useState('')

  useEffect(() => {
    const n = meQ.data?.display_name || user?.display_name || ''
    setDisplayName(n)
  }, [meQ.data?.display_name, user?.display_name])

  const runOrStepUp = useCallback(async (fn: () => Promise<void>) => {
    setError(null)
    setBusy(true)
    try {
      await fn()
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setNeedStepUp(() => () => {
          void (async () => {
            setBusy(true)
            try {
              await fn()
            } catch (e2) {
              setError(e2 instanceof ApiError ? e2.message : t('profile.actionFailed'))
            } finally {
              setBusy(false)
            }
          })()
        })
      } else {
        setError(err instanceof ApiError ? err.message : t('profile.actionFailed'))
      }
    } finally {
      setBusy(false)
    }
  }, [t])

  async function saveName() {
    setBusy(true)
    setError(null)
    try {
      const res = await patchProfile({ display_name: displayName.trim() || null })
      const snap = getSnapshot()
      if (snap.user && snap.accessToken) {
        setSession({
          accessToken: snap.accessToken,
          user: { ...snap.user, display_name: res.display_name },
          amr: snap.amr,
          sessionId: snap.sessionId,
        })
      }
      setOkMsg(t('profile.saved'))
      void meQ.refetch()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('profile.actionFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function onUpload(file: File | null) {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      await uploadMyAvatar(file)
      const snap = getSnapshot()
      if (snap.user && snap.accessToken) {
        setSession({
          accessToken: snap.accessToken,
          user: { ...snap.user, has_avatar: true },
          amr: snap.amr,
          sessionId: snap.sessionId,
        })
      }
      onAvatarChange()
      setOkMsg(t('profile.avatarUpdated'))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('profile.avatarFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function onRemoveAvatar() {
    setBusy(true)
    setError(null)
    try {
      await deleteMyAvatar()
      const snap = getSnapshot()
      if (snap.user && snap.accessToken) {
        setSession({
          accessToken: snap.accessToken,
          user: { ...snap.user, has_avatar: false },
          amr: snap.amr,
          sessionId: snap.sessionId,
        })
      }
      onAvatarChange()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('profile.actionFailed'))
    } finally {
      setBusy(false)
    }
  }

  const langOptions =
    (langsQ.data?.languages || []).length > 0
      ? [...(langsQ.data?.languages || [])]
          .map((l) => ({
            code: l.code,
            label: l.name_native || l.name_en || l.code,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, locale || 'en', { sensitivity: 'base' }))
      : [
          { code: 'bs', label: 'Bosanski' },
          { code: 'hr', label: 'Hrvatski' },
          { code: 'sr', label: 'Srpski' },
          { code: 'en', label: 'English' },
          { code: 'de', label: 'Deutsch' },
        ]
  const selectValue =
    langOptions.find((l) => l.code === locale || l.code === i18n.language)?.code ||
    langOptions.find((l) => l.code.split('-')[0] === (locale || '').split('-')[0])?.code ||
    langOptions[0]?.code ||
    'en'

  const sessions: AuthSessionRow[] = sessionsQ.data?.sessions || []
  const passkeys = passkeysQ.data?.credentials || []

  return (
    <div className="rd-sec-body rd-profile">
      {error ? (
        <p className="warn" role="alert">
          {error}
        </p>
      ) : null}
      {okMsg ? <p className="ok">{okMsg}</p> : null}

      <div className="rd-card">
        <h3>{t('profile.cardProfile')}</h3>
        <div className="rd-profile-ava-row">
          <UserAvatar size={72} revision={avatarRevision} />
          <div className="rd-profile-ava-actions">
            <label className="rd-rbtn2">
              <Icon name="upload" size={16} /> {t('profile.uploadAvatar')}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                hidden
                disabled={busy}
                onChange={(e) => void onUpload(e.target.files?.[0] || null)}
              />
            </label>
            <button type="button" className="rd-rbtn2" disabled={busy} onClick={() => void onRemoveAvatar()}>
              {t('profile.removeAvatar')}
            </button>
          </div>
        </div>
        <label className="rd-profile-field">
          {t('profile.displayName')}
          <input
            type="text"
            maxLength={80}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
        <label className="rd-profile-field">
          {t('auth.email')}
          <input type="email" value={user?.email || meQ.data?.email || ''} readOnly />
        </label>
        <button type="button" className="rd-rbtn2 ai" disabled={busy} onClick={() => void saveName()}>
          {t('profile.save')}
        </button>
      </div>

      <div className="rd-card">
        <h3>{t('profile.cardLanguage')}</h3>
        <p className="d">{t('profile.languageNote')}</p>
        <label className="rd-profile-field rd-profile-lang-select">
          <span className="visually-hidden">{t('profile.cardLanguage')}</span>
          <select
            value={selectValue}
            onChange={(e) => setLocale(e.target.value)}
            aria-label={t('profile.cardLanguage')}
          >
            {langOptions.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="rd-card">
        <h3>{t('profile.cardSecurity')}</h3>
        <p className="d">{t('profile.noPassword')}</p>
        <p className="mail-set-note">
          AMR: {(amr || meQ.data?.amr || []).join(', ') || '—'} · passkeys: {passkeys.length}
        </p>

        <h4>{t('profile.passkeys')}</h4>
        <ul className="rd-profile-list">
          {passkeys.map((p) => (
            <li key={p.id}>
              <span>
                <strong>{p.friendly_name || p.id.slice(0, 8)}</strong>
                <span className="muted"> · {p.created_at?.slice(0, 10)}</span>
              </span>
              <button
                type="button"
                className="rd-rbtn2"
                disabled={busy}
                onClick={() =>
                  void runOrStepUp(async () => {
                    await revokePasskey(p.id)
                    await passkeysQ.refetch()
                  })
                }
              >
                {t('auth.revoke')}
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="rd-rbtn2"
          disabled={busy}
          onClick={() =>
            void runOrStepUp(async () => {
              await registerPasskey('Device')
              setOkMsg(t('auth.registerOk'))
              await passkeysQ.refetch()
            })
          }
        >
          {t('auth.registerPasskey')}
        </button>

        <h4>{t('profile.recovery')}</h4>
        <div className="rd-profile-row">
          <button
            type="button"
            className="rd-rbtn2"
            disabled={busy}
            onClick={() =>
              void runOrStepUp(async () => {
                try {
                  const res = await enrollRecoveryCodes()
                  setCodes(res.codes)
                } catch (err) {
                  if (err instanceof ApiError && err.status === 403) {
                    const res = await rotateRecoveryCodes()
                    setCodes(res.codes)
                  } else throw err
                }
              })
            }
          >
            {t('auth.showRecoveryCodes')}
          </button>
          <button
            type="button"
            className="rd-rbtn2"
            disabled={busy}
            onClick={() =>
              void runOrStepUp(async () => {
                const res = await rotateRecoveryCodes()
                setCodes(res.codes)
              })
            }
          >
            {t('profile.rotateCodes')}
          </button>
        </div>
        {codes ? (
          <div className="rd-profile-codes">
            <p className="warn">{t('auth.codesOnce')}</p>
            <ul>
              {codes.map((c) => (
                <li key={c}>
                  <code>{c}</code>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <h4>TOTP</h4>
        <div className="rd-profile-row">
          <button
            type="button"
            className="rd-rbtn2"
            disabled={busy}
            onClick={() =>
              void (async () => {
                setBusy(true)
                try {
                  const res = await totpStart()
                  setTotpSecret(res.secret)
                  setTotpUri(res.otpauth_uri)
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : t('profile.actionFailed'))
                } finally {
                  setBusy(false)
                }
              })()
            }
          >
            {t('profile.totpSetup')}
          </button>
        </div>
        {totpUri ? (
          <div className="rd-profile-totp">
            <p className="d">
              <code>{totpSecret}</code>
            </p>
            <p className="mail-set-note">{totpUri}</p>
            <label className="rd-profile-field">
              {t('auth.totpOptional')}
              <input value={totpCode} onChange={(e) => setTotpCode(e.target.value)} />
            </label>
            <button
              type="button"
              className="rd-rbtn2 ai"
              disabled={busy || totpCode.trim().length < 6}
              onClick={() =>
                void (async () => {
                  setBusy(true)
                  try {
                    await totpConfirm(totpCode.trim())
                    setOkMsg(t('profile.totpOk'))
                    setTotpUri(null)
                    setTotpSecret(null)
                    setTotpCode('')
                  } catch (err) {
                    setError(err instanceof ApiError ? err.message : t('profile.actionFailed'))
                  } finally {
                    setBusy(false)
                  }
                })()
              }
            >
              {t('profile.totpConfirm')}
            </button>
          </div>
        ) : null}

        <h4>{t('auth.sessionsTitle')}</h4>
        <ul className="rd-profile-list">
          {sessions.map((s) => (
            <li key={s.id}>
              <div>
                <strong>{s.device_label || s.user_agent || s.id.slice(0, 8)}</strong>
                {s.current ? <span className="pill">{t('auth.currentSession')}</span> : null}
                <p className="muted">
                  {(s.amr || []).join(', ') || '—'} · {s.ip_prefix || 'ip?'}
                </p>
              </div>
              {!s.current ? (
                <button
                  type="button"
                  className="rd-rbtn2"
                  disabled={busy}
                  onClick={() =>
                    void runOrStepUp(async () => {
                      await revokeSession(s.id)
                      await sessionsQ.refetch()
                    })
                  }
                >
                  {t('auth.revoke')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="rd-rbtn2"
          disabled={busy}
          onClick={() =>
            void runOrStepUp(async () => {
              await logoutAll()
              window.location.assign('/welcome')
            })
          }
        >
          {t('auth.logoutAll')}
        </button>
      </div>

      <div className="rd-card">
        <h3>{t('profile.cardTokens')}</h3>
        <p className="d">{t('profile.tokensNote')}</p>
        <p className="mail-set-note">{t('profile.tokensBalance', { balance: '—' })}</p>
        <button type="button" className="rd-rbtn2" disabled title={t('mail.soon')}>
          {t('profile.tokensLink')}
        </button>
      </div>

      <div className="rd-card">
        <h3>{t('profile.cardData')}</h3>
        <p className="d">{t('profile.dataNote')}</p>
        <button type="button" className="rd-rbtn2" disabled>
          {t('profile.exportSoon')}
        </button>
      </div>

      {needStepUp ? (
        <StepUpModal
          onCancel={() => setNeedStepUp(null)}
          onVerified={() => {
            const fn = needStepUp
            setNeedStepUp(null)
            fn()
          }}
          runStepUp={stepUpPasskey}
        />
      ) : null}
    </div>
  )
}
